import { useEffect, useRef } from 'react'
import _ from 'lodash'
import uuid from 'uuid'

const getDefaultServing = servings => {
  let defaultServing = 4
  if (!servings.includes(4)) {
    defaultServing = servings.sort()[Math.floor(servings.length / 2)]
  }
  return defaultServing
}

export const getServingsInfo = (servings, currentServings, unitsList, prevDefaultServing) => {

  const defaultServing = getDefaultServing(servings)
  let newQuantity = 0
  if (prevDefaultServing !== undefined && defaultServing !== prevDefaultServing) {
    const lastQuantity = _.get(currentServings, `${prevDefaultServing}.quantity`, 0)
    newQuantity = (lastQuantity / prevDefaultServing) * defaultServing
  }
  const quantity = _.get(currentServings, `${defaultServing}.quantity`, newQuantity)
  const step = Math.round(quantity / defaultServing)
  const unitID = _.get(currentServings, `${defaultServing}.unitID`, unitsList[0].id)

  return {
    step,
    unitID,
    quantity,
    defaultServing,
  }
}

const updateIngredientServing = (ingredient, servings, unitsList) => {
  let changed = false
  if (!ingredient.servings) {
    return false
  }

  const prevDefaultServing = getDefaultServing(Reflect.ownKeys(ingredient.servings).map(s => parseInt(s, 10)))
  const { step, unitID } = getServingsInfo(servings, ingredient.servings, unitsList, prevDefaultServing)
  for (let i = 1; i < 9; i++) {
    if(!servings.includes(i) && ingredient.servings[i]) {
      delete ingredient.servings[i]
      changed = true
    } else if(servings.includes(i) && !ingredient.servings[i]){
      ingredient.servings[i] = {
        quantity: step * i,
        unitID,
      }
      changed = true
    }
  }
  return changed
}

export const updateServings = (servings, prevServings, variations = [], batchChange, unitsList) => {

  const toChange = [...variations]
  let changed = false

  toChange.forEach(variation => {
    (variation.sections || []).forEach(section => {
      (section.ingredients || []).forEach(ingredient => {
        const hasChanges = updateIngredientServing(ingredient, servings, unitsList)
        if (hasChanges) {
          changed = true
        }
        if (ingredient.hasReplacement) {
          (ingredient.replacements || []).forEach(replacement => {
            const hasChangesInReplacement = updateIngredientServing(replacement, servings, unitsList)
            if (hasChangesInReplacement) {
              changed = true
            }
          })
        }
      })
    })
  })

  if (changed) {
    batchChange({ values: toChange })
  }
}

export const usePrevious = value => {
  const ref = useRef()
  useEffect(() => {
    ref.current = value
  })
  return ref.current
}

const iterateIngredientSteps = (model, ingredient, variationField, callback) => {
  const appliances = Reflect.ownKeys(_.get(model, `values[${variationField}].applianceSteps`, {}))
  appliances.forEach(appliance => {
    const steps = _.get(model, `values[${variationField}].applianceSteps.${appliance}.preparationSteps`, [])
    steps.forEach((step, index) => {
      if((step.newIngredientKey && step.newIngredientKey === ingredient.newIngredientKey) ||
      (
        step.recipeIngredientID && step.recipeIngredientID === ingredient.recipeIngredientID
      )) {
        callback(step, index, appliance)
      }
    })
  })
}

export const linkedIngredientSteps = (model, ingredient, variationField) => {
  const result = []
  if (ingredient) {
    iterateIngredientSteps(model, ingredient, variationField, step => result.push(step))
  }
  return result
}

export const clearStepsFromIngredient = (model, ingredient, replacement, variationField, modifyUtils) => {
  if (!ingredient) {
    return model
  }

  const appliances = Reflect.ownKeys(_.get(model, `values[${variationField}].applianceSteps`, {}))
  appliances.forEach(appliance => {
    const steps = _.get(model, `values[${variationField}].applianceSteps.${appliance}.preparationSteps`, [])
    const stepsToRemove = []
    const replacementsToRemove = []
    steps.forEach((step, index) => {
      if((step.newIngredientKey && step.newIngredientKey === ingredient.newIngredientKey) ||
      (
        step.recipeIngredientID && step.recipeIngredientID === ingredient.recipeIngredientID
      )) {
        if (replacement) {
          step.replacements.forEach((replacementStep, i) => {
            if((replacementStep.newIngredientKey && replacementStep.newIngredientKey === replacement.newIngredientKey) ||
            (
              replacementStep.recipeIngredientID && replacementStep.recipeIngredientID === replacement.recipeIngredientID
            )) {
              replacementsToRemove.push({
                step,
                index,
                replacementStep,
                replacementStepIndex: i,
              })
            }
          })
        } else {
          stepsToRemove.push({
            step,
            index,
          })
        }
      }
    })
    if (stepsToRemove && stepsToRemove.length) {
      const modifiedSteps = steps.filter(s => {
        const shouldRemove = stepsToRemove.find(sr => sr.step === s)
        if (shouldRemove) {
          modifyUtils.correctErrorsAfterDeleteItem(model, `values[${variationField}].applianceSteps.${appliance}.preparationSteps`, shouldRemove.index)
        }
        return !shouldRemove
      })
      _.set(model, `values[${variationField}].applianceSteps.${appliance}.preparationSteps`, modifiedSteps)
    } else if(replacementsToRemove && replacementsToRemove.length) {
      replacementsToRemove.forEach(({ step, index, replacementStep, replacementStepIndex }) => {
        const filtered = step.replacements.filter(sr => sr !== replacementStep)
        step.replacements = filtered && filtered.length ? filtered : undefined
        modifyUtils.correctErrorsAfterDeleteItem(model,
          `values[${variationField}].applianceSteps.${appliance}.preparationSteps[${index}].replacements`,
          replacementStepIndex)
      })
    }
  })

  return model
}

const replaceIngredientId = (store, ingredient) => {
  if (ingredient.recipeIngredientID) {
    const newId = uuid()
    store.recipeIngredientIDs[ingredient.recipeIngredientID] = newId
    delete ingredient.recipeIngredientID
    ingredient.newIngredientKey = newId
  }
  if (ingredient.hasReplacement && ingredient.replacements && ingredient.replacements.length) {
    ingredient.replacements.forEach(replacement => replaceIngredientId(store, replacement))
  }
}

const replaceStepId = (store, step, ignoreIngredients = false, forceNewId = false) => {
  if (step.stepID) {
    const newId = uuid()
    store.stepsIDs[step.stepID] = newId
    delete step.stepID
    step.stepNewKey = newId
  } else if (step.stepNewKey && forceNewId) {
    const newId = uuid()
    store.stepNewKeys[step.stepNewKey] = newId
    delete step.stepID
    step.stepNewKey = newId
  }

  if (!ignoreIngredients && step.recipeIngredientID) {
    step.newIngredientKey = store.recipeIngredientIDs[step.recipeIngredientID]
    delete step.recipeIngredientID
  }

  if (step.replacements && step.replacements.length) {
    step.replacements.forEach(replacement => replaceStepId(store, replacement, ignoreIngredients, forceNewId))
  }
}

const fixJumps = (store, step, updateNewRefs = false) => {
  if (step.stepJump1ID) {
    step.stepJump1NewKey = store.stepsIDs[step.stepJump1ID]
    delete step.stepJump1ID
  } else if (step.stepJump1NewKey && updateNewRefs) {
    step.stepJump1NewKey = store.stepNewKeys[step.stepJump1NewKey]
  }

  if (step.stepJump2ID) {
    step.stepJump2NewKey = store.stepsIDs[step.stepJump2ID]
    delete step.stepJump2ID
  } else if (step.stepJump2NewKey && updateNewRefs) {
    step.stepJump2NewKey = store.stepNewKeys[step.stepJump2NewKey]
  }
}

const safeArray = a => a || []

export const copyVariation = variation => {
  const copy = JSON.parse(JSON.stringify(variation))
  const keysStore = {
    recipeIngredientIDs: {},
    stepsIDs: {},
    stepNewKeys:{},
  }

  delete copy.id
  copy.isMain = false

  copy.sections.forEach(section => {
    delete section.sectionID
    section.ingredients.forEach(ingredient => replaceIngredientId(keysStore, ingredient))
  })

  Reflect.ownKeys(copy.applianceSteps).forEach(applianceID => {
    const appliance = copy.applianceSteps[applianceID] || {}
    safeArray(appliance.initialSteps).forEach(step => replaceStepId(keysStore, step))
    safeArray(appliance.finalSteps).forEach(step => replaceStepId(keysStore, step))
    safeArray(appliance.preparationSteps).forEach(step => replaceStepId(keysStore, step))
    safeArray(appliance.cookingSteps).forEach(step => replaceStepId(keysStore, step))

    safeArray(appliance.initialSteps).forEach(step => fixJumps(keysStore, step))
    safeArray(appliance.finalSteps).forEach(step => fixJumps(keysStore, step))
    safeArray(appliance.preparationSteps).forEach(step => fixJumps(keysStore, step))
    safeArray(appliance.cookingSteps).forEach(step => fixJumps(keysStore, step))
  })
  return copy
}

export const copyAppliance = appliance => {
  const copy = JSON.parse(JSON.stringify(appliance))

  const keysStore = {
    stepsIDs: {},
    stepNewKeys:{},
  }

  safeArray(copy.initialSteps).forEach(step => replaceStepId(keysStore, step, true, true))
  safeArray(copy.finalSteps).forEach(step => replaceStepId(keysStore, step, true, true))
  safeArray(copy.preparationSteps).forEach(step => replaceStepId(keysStore, step, true, true))
  safeArray(copy.cookingSteps).forEach(step => replaceStepId(keysStore, step, true, true))

  safeArray(copy.initialSteps).forEach(step => fixJumps(keysStore, step, true))
  safeArray(copy.finalSteps).forEach(step => fixJumps(keysStore, step, true))
  safeArray(copy.preparationSteps).forEach(step => fixJumps(keysStore, step, true))
  safeArray(copy.cookingSteps).forEach(step => fixJumps(keysStore, step, true))

  return copy
}
